import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Target, TrendingUp, Car } from "lucide-react";
import Layout from "@/components/Layout";
import { useI18n } from "@/i18n";
import { getServiceBySlug, services } from "@/data/services";
import { stock } from "@/data/brand";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const s = getServiceBySlug(slug);

  // Per-service JSON-LD schema (Service + BreadcrumbList).
  useEffect(() => {
    if (!s) return;
    const canonicalUrl = `https://madturbo.lt/services/${s.slug}`;
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${canonicalUrl}#service`,
      name: s.title.en,
      alternateName: s.title.lt,
      description: (lang === "lt" ? s.short.lt : s.short.en) || s.short.en,
      serviceType: s.category,
      url: canonicalUrl,
      provider: { "@id": "https://madturbo.lt/#business" },
      areaServed: [
        { "@type": "Country", name: "Lithuania" },
        { "@type": "Country", name: "Latvia" },
        { "@type": "Country", name: "Estonia" },
        { "@type": "Country", name: "Poland" },
      ],
      availableLanguage: ["Lithuanian", "English"],
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: canonicalUrl,
      },
    };
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "MAD TURBO", item: "https://madturbo.lt/" },
        { "@type": "ListItem", position: 2, name: lang === "lt" ? "Paslaugos" : "Services", item: "https://madturbo.lt/services" },
        { "@type": "ListItem", position: 3, name: lang === "lt" ? s.title.lt : s.title.en, item: canonicalUrl },
      ],
    };

    const svcTag = document.createElement("script");
    svcTag.type = "application/ld+json";
    svcTag.setAttribute("data-mt-schema", "service");
    svcTag.textContent = JSON.stringify(serviceSchema);
    document.head.appendChild(svcTag);

    const bcTag = document.createElement("script");
    bcTag.type = "application/ld+json";
    bcTag.setAttribute("data-mt-schema", "breadcrumb");
    bcTag.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(bcTag);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"][data-mt="service"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      canonical.setAttribute("data-mt", "service");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    return () => {
      document.querySelectorAll('script[data-mt-schema="service"], script[data-mt-schema="breadcrumb"]').forEach((n) => n.remove());
      const c = document.querySelector('link[rel="canonical"][data-mt="service"]');
      if (c) c.remove();
    };
  }, [s, lang]);

  if (!s) return <Navigate to="/services" replace />;

  const related = (s.related || [])
    .map((r) => services.find((x) => x.slug === r))
    .filter(Boolean);

  const title = lang === "lt" ? s.title.lt : s.title.en;
  const short = lang === "lt" ? s.short.lt : s.short.en;
  const intro = lang === "lt" ? s.intro.lt : s.intro.en;
  const benefits = lang === "lt" ? s.benefits.lt : s.benefits.en;
  const suitable = lang === "lt" ? s.suitable.lt : s.suitable.en;
  const results = lang === "lt" ? s.results.lt : s.results.en;
  const category = t(`services.category${s.category.charAt(0).toUpperCase() + s.category.slice(1)}`);

  return (
    <Layout seoTitle={title} seoDescription={short}>
      {/* Hero */}
      <section className="relative pt-14 pb-16 md:pt-20 md:pb-24 border-b border-white/[0.06] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={stock.engineDetails} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-[0.45]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />
        </div>
        <div className="container-mt relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/services" data-testid="back-to-services" className="inline-flex items-center gap-2 text-zinc-400 hover:text-brand-lime text-[11px] font-mono uppercase tracking-[0.22em] transition-colors">
              <ArrowLeft size={13} /> {t("common.backToServices")}
            </Link>
            <span className="h-px w-8 bg-zinc-800" />
            <span className="tech-chip tech-chip--lime">{category}</span>
          </div>
          <h1 className="h-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-white max-w-4xl mb-6 leading-[0.95]">
            {title}
          </h1>
          <p className="text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed">{short}</p>
        </div>
      </section>

      {/* Body */}
      <section className="section-y">
        <div className="container-mt grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-2 space-y-14 md:space-y-16">
            <div>
              <p className="kicker mb-5"><span className="kicker__bar" /><span>{t("labels.overview")}</span></p>
              <p className="text-zinc-200 text-lg md:text-xl leading-relaxed font-light">{intro}</p>
            </div>

            <div>
              <p className="kicker mb-5"><span className="kicker__bar" /><span>{t("common.benefits")}</span></p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 card-mt card-mt-pad !p-4">
                    <CheckCircle2 size={16} className="text-brand-lime mt-1 shrink-0" />
                    <span className="text-zinc-200 text-sm leading-snug">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="card-mt card-mt-pad">
                <div className="icon-tile mb-4"><Car size={18} /></div>
                <p className="kicker mb-3"><span>{t("common.suitableVehicles")}</span></p>
                <p className="text-zinc-300 leading-relaxed text-sm">{suitable}</p>
              </div>
              <div className="card-mt card-mt-pad">
                <div className="icon-tile mb-4"><TrendingUp size={18} /></div>
                <p className="kicker mb-3"><span>{t("common.expectedResults")}</span></p>
                <p className="text-zinc-300 leading-relaxed text-sm">{results}</p>
              </div>
            </div>
          </div>

          {/* Sticky CTA */}
          <aside className="lg:sticky lg:top-28">
            <div className="card-mt card-mt-pad">
              <div className="icon-tile mb-5"><Target size={18} /></div>
              <p className="kicker mb-3"><span>MAD TURBO</span></p>
              <h2 className="h-display text-2xl md:text-3xl text-white mb-3">{t("common.ctaTitle")}</h2>
              <p className="text-zinc-400 text-sm mb-6 leading-relaxed">{t("common.ctaText")}</p>
              <Link to="/contact" data-testid="service-cta-book" className="btn-primary w-full justify-center">
                {t("common.bookConsultation")} <ArrowRight size={14} />
              </Link>
              <div className="divider-mt my-6" />
              <p className="eyebrow mb-3">{t("labels.category")}</p>
              <span className="tech-chip tech-chip--lime">{category}</span>
            </div>
          </aside>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="section-y border-t border-white/[0.06]">
          <div className="container-mt">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
              <div>
                <p className="kicker mb-4"><span className="kicker__bar" /><span>{t("labels.related")}</span></p>
                <h2 className="h-display text-3xl md:text-4xl text-white">{t("common.relatedServices")}</h2>
              </div>
              <Link to="/services" className="btn-outline shrink-0">
                {t("common.viewServices")} <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {related.map((r, i) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  data-testid={`service-card-${r.slug}`}
                  className="card-mt card-mt-pad card-interactive group flex flex-col"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="num-chip">{String(i + 1).padStart(2, "0")}</span>
                    <span className="eyebrow text-[10px]">{t(`services.category${r.category.charAt(0).toUpperCase() + r.category.slice(1)}`)}</span>
                  </div>
                  <h3 className="h-display text-xl md:text-2xl text-white mb-3 leading-tight">
                    {lang === "lt" ? r.title.lt : r.title.en}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-5 flex-1">{lang === "lt" ? r.short.lt : r.short.en}</p>
                  <span className="inline-flex items-center gap-2 text-brand-lime font-mono uppercase text-[10px] tracking-[0.22em] mt-auto">
                    {t("common.readMore")} <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
